import { useCallback } from 'react';

import { cloneDeep, isEmpty } from 'lodash';

import { SCHEDULE_TYPE } from '../common/scheduleMode';
import { TState } from '../schedule';

const WEEK_DAYS_COUNT = 7;
/** В недельном режиме по умолчанию рабочие — первые пять дней. */
const DEFAULT_WORKING_DAYS = 5;

export function useResetEDs(state: TState) {
    const resetEDs = useCallback(
        (
            on: number = state.schedule?.schedule_days?.length ?? 0,
            off: number = state.schedule?.day_offs?.length ?? 0
        ) => {
            if (state.schedule == null) return;

            state.intOn = on;
            state.intOff = off;

            const isWeekly = state.schedule.schedule_type === SCHEDULE_TYPE.WEEKDAY;
            const limit = isWeekly ? WEEK_DAYS_COUNT : state.intOn + state.intOff;

            const teds = [];

            for (let i = 0; i < limit; i++) {
                const dayIndex = i + 1;
                const existingED = state.schedule.exception_days.find((edi) => edi.day == dayIndex);

                if (existingED) {
                    teds.push(existingED);
                    continue;
                }

                teds.push({
                    day: '' + dayIndex,
                    start_time: state.schedule.start_time,
                    end_time: state.schedule.end_time,
                    breaks: state.schedule.breaks,
                    is_day_off: getIsDayOff(state, dayIndex, i, on),
                });
            }

            state.eds = teds;
            state.edsImmutable = cloneDeep(teds);
            state.tempBreaks = teds.map(() => ({
                start_break_time: null,
                end_break_time: null,
            }));
        },
        [state]
    );

    return { resetEDs };
}

/**
 * Выходной ли день по порядковому номеру.
 *
 * Режим «Каждый день» проверяем раньше `day_offs`: это неделя без выходных,
 * а у графиков, сохранённых старой версией фронта с несуществующим типом 2,
 * в `day_offs` мог остаться мусор от прошлого режима.
 */
function getIsDayOff(state: TState, dayIndex: number, index: number, workingDays: number): number {
    if (state.scheduleMode === 'daily') return 0;
    if (state.schedule.day_offs?.includes(dayIndex)) return 1;

    // У сохранённого графика состав дней уже известен: раз дня нет в day_offs — он рабочий.
    // Подставлять 5/2 можно только пустой форме, иначе суббота и воскресенье возвращались
    // бы в выходные при каждой перезагрузке, чего бы пользователь ни сохранил.
    if (hasStoredDays(state)) return 0;

    return state.schedule.schedule_type === SCHEDULE_TYPE.WEEKDAY
        ? Number(index >= DEFAULT_WORKING_DAYS)
        : Number(index >= workingDays);
}

/** У графика есть сохранённый состав дней, а не пустая заготовка формы создания. */
function hasStoredDays(state: TState): boolean {
    return !isEmpty(state.schedule.schedule_days) || !isEmpty(state.schedule.day_offs);
}
